<template>
  <div v-if="packSizes" class="PackSize__Container">
    <span class="p4 PackSize__Title">{{
      store?.$state?.terms?.bmsm?.title
    }}</span>
    <Tooltip :text="bmsmTooltipText" />
    <div class="PackSize__Buttons">
      <div
        v-for="tier in packSizes"
        :key="tier.threshold"
        class="PackSize__ButtonsWrap"
      >
        <button
          class="button button--tertiary-a"
          data-ht-element="pack-size"
          :class="{
            PackSize__Button: true,
            'PackSize__Button--Active': quantity == tier.threshold,
          }"
          @click="updatePackSize(tier.threshold)"
        >
          {{ getPackSizeText(tier.threshold) }}
        </button>
        <span
          v-if="tier.discount"
          class="t5 PackSize__Savings"
          data-color="green"
          >{{ store?.$state?.terms?.bmsm?.discount_text }}
          {{ tier.discount }}%</span
        >
      </div>
    </div>
  </div>
  <div
    v-if="(!store.$state.isBundle || isQV) && !available"
    class="ProductForm__Actions"
  >
    <div class="ProductForm__ActionWrap">
      <BackInStock :location="location" :qv-data="qvBISData" />
      <WishlistButton
        :empi="productId"
        :epi="variantId"
        :dt="data.item.title"
        :du="url"
        :cprops="{ selectedOption, handle: data.item.handle }"
        :quick-view="isQV"
      />
    </div>
  </div>
  <template v-else>
    <div
      class="ProductForm__Actions"
      :class="{
        'ProductForm__Actions--ReviewBundle': isReviewStep && !isQV,
        'ProductForm__Actions--Building': isBuilding && !isQV,
      }"
    >
      <div class="ProductForm__ActionWrap">
        <template v-if="edit && !isQV">
          <div class="ProductForm__Cta--Container">
            <button
              role="button"
              class="ProductForm__Cta"
              :class="{
                'ProductForm__Cta--Inactive': !available,
              }"
              @click="handleEdit"
            >
              {{ cta }}
            </button>
            <Backorder v-if="shipsBy" :date="shipsBy" />
            <div v-if="optionsToSelectError" class="ProductForm__CtaCaption">
              <p class="p5" data-color="magic-hour-error">
                {{ optionsToSelectError }}
              </p>
            </div>
          </div>
        </template>
        <template v-else-if="isBuildStep && !isQV">
          <div class="ProductForm__Cta--Container">
            <button
              role="button"
              class="ProductForm__Cta ProductForm__Cta--Start"
              @click="store.step('+')"
            >
              {{ cta }}
            </button>
          </div>
          <WishlistButton
            :empi="formatID(data.item.id)"
            :epi="data.selected.variant"
            :du="url"
            :dt="data.item.title"
            :quick-view="isQV"
            :cprops="{ selectedOption, handle: data.item.handle }"
          />
        </template>
        <template v-else-if="isBuilding && !isQV">
          <div class="ProductForm__Cta--Container">
            <button
              role="button"
              class="ProductForm__Cta ProductForm__Cta--Prev ProductForm__Cta--Inverted"
              @click="store.step('-')"
            >
              {{ store.terms.cta.prev }}
            </button>
            <Backorder v-if="shipsBy" :date="shipsBy" />
            <div v-if="optionsToSelectError" class="ProductForm__CtaCaption">
              <p class="p5" data-color="magic-hour-error">
                {{ optionsToSelectError }}
              </p>
            </div>
          </div>

          <button
            role="button"
            class="ProductForm__Cta ProductForm__Cta--Next"
            :disabled="readyToProceed !== ''"
            :class="readyToProceed"
            @click="store.step('+')"
          >
            {{ nextCta }}
          </button>
        </template>
        <template v-else>
          <div class="ProductForm__Cta--Container">
            <button
              role="button"
              class="ProductForm__Cta"
              data-ht-element="cta"
              :class="{
                'ProductForm__Cta--Inactive': !available,
                'Rise-add-to-cart-button': isGiftCard,
              }"
              @click="handleSubmit"
            >
              {{ cta }}
            </button>
            <Backorder v-if="shipsBy" :date="shipsBy" />
            <div v-if="optionsToSelectError" class="ProductForm__CtaCaption">
              <p class="p5" data-color="magic-hour-error">
                {{ optionsToSelectError }}
              </p>
            </div>
          </div>
          <div
            v-if="!packSizes && !isGiftCard"
            class="select-input--wrapper select-input--wrapper-pdp"
          >
            <select
              v-model="quantity"
              name="quantity"
              aria-label="quantity selector"
              data-ht-element="quantity-input"
              class="select-input p_2 p_2--bold"
            >
              <option
                v-for="index in maxAllowedQuantity"
                :key="index"
                :value="index"
              >
                {{ index }}
              </option>
            </select>
            <Icon :icon="'chevron-down'" />
          </div>
          <WishlistButton
            v-if="!isReviewStep"
            :empi="productId"
            :epi="variantId"
            :du="url"
            :dt="data.item.title"
            :cprops="{ selectedOption, handle: data.item.handle }"
            :quick-view="isQV"
          />
        </template>
      </div>
    </div>
  </template>
</template>

<script setup>
import { computed, ref, watchEffect, onMounted } from 'vue';
import Backorder from './Backorder.vue';
import { useProductStore } from './Composables/Store';
import { fetchStorefront, clean } from '@utils/GraphQLClient';
import {
  COMPONENT_FROM_SELECTIONS_QUERY,
  CHECKOUT_PRODUCT_QUERY,
} from './Composables/Queries';
import Tooltip from '../Global/Tooltip.vue';
import Icon from '../Global/Icon.vue';
import { formatID, parseTag } from '@utils/format';
import BackInStock from './BackInStock.vue';
import WishlistButton from '../Global/WishlistButton.vue';

const emits = defineEmits(['close']);
const props = defineProps({
  data: { type: Object, required: false },
  edit: { type: Boolean, required: false, default: false },
  isAutoBundle: { type: Boolean, required: false, default: false },
  isReviewStep: { type: Boolean, required: false, default: false },
  isModal: { type: Boolean, required: false, default: false },
  isGiftCard: { type: Boolean, required: false, default: false },
  location: { type: String },
  selectedProduct: { type: [Object, Boolean], default: false },
});

const cta = ref('');
const quantity = ref(1);
const ctaEl = ref(false);
const optionsToSelectError = ref(false);

const store = useProductStore();
const isBuildStep = computed(() => store.isBuildStep);
const isBuilding = computed(() => store.isBuilding);
const isQV = computed(() => props.location == 'quick-view');

const productId = computed(() => {
  if (props?.data?.selected?.variant) {
    return formatID(props?.data?.selected?.variant?.product?.id);
  }

  if (props.selectedProduct) {
    return formatID(props.selectedProduct.id);
  } else {
    return formatID(props?.data?.item?.id);
  }
});

const selectedOption = computed(() => {
  const item = props?.data?.item ?? {};
  const tags = item?.tags ?? [];
  const galleryTag = parseTag(tags, 'PDP Gallery Option');
  const visualIndex = item?.options.findIndex(
    (option) => option.name === galleryTag
  );

  return props?.data?.selected?.options[visualIndex]?.value ?? '';
});

const variantId = computed(() => {
  if (props?.data?.selected?.variant) {
    const id = props?.data?.selected?.variant?.id;

    if (!id) return props?.data?.selected?.variant;

    return formatID(id);
  } else {
    const matches = props?.data?.selected?.matches ?? [];
    const hasMatches = matches.length > 0;

    if (hasMatches) return formatID(matches[0].id);

    return '';
  }
});

const url = computed(() => {
  if (props?.data?.item) {
    if (variantId.value !== '') {
      return `/products/${props.data.item.handle}/?variant=${variantId.value}`;
    } else {
      return `/products/${props.data.item.handle}`;
    }
  } else {
    return '';
  }
});

const qvBISData = computed(() => {
  const bisData = {
    variantId: props.data.selected.variant.id,
    productId: props.data.selected.variant.product.id,
    url: props.data.item.onlineStoreUrl,
    img: props.data.gallery[0].image.url,
    price: parseFloat(props.data.item.priceRange.minVariantPrice.amount),
  };
  return bisData;
});

//BMSM Pack Sizes + Pack Size Quantity Logic

const updatePackSize = (packSize) => {
  const queryString = window.location.search;
  const params = new URLSearchParams(queryString);
  const defaultPackSize =
    findTiers().filter((tier) => tier.threshold == params.get('pack-size'))
      .length > 0
      ? params.get('pack-size')
      : findTiers()[0].threshold;
  params.set('pack-size', packSize || defaultPackSize);
  quantity.value = params.get('pack-size');
  if (!isQV.value) {
    const url = `${window.location.pathname}?${params}`;
    window.history.replaceState({}, '', url);
  }
};

const packSizes = computed(() => {
  if (props?.location && props?.location.includes('auto-bundle')) {
    return false;
  }
  if (findTiers().length > 0) updatePackSize();
  return findTiers().length > 0 ? findTiers() : false;
});

/**
 * Parses threshold-keyed labels from the BMSM Labels tag
 * @param {Array<string>} tags - Product tags array
 * @returns {Object|null} Map of threshold → label text, or null if tag absent
 */
const parseBmsmLabels = (tags) => {
  const tagValue = parseTag(tags, 'BMSM Labels');
  if (!tagValue) return null;

  const labels = {};
  const tiers = tagValue.split('/');

  tiers.forEach((tier) => {
    const separatorIndex = tier.indexOf('-');
    if (separatorIndex === -1) return;

    const threshold = tier.substring(0, separatorIndex).trim();
    const label = tier.substring(separatorIndex + 1).trim();
    labels[threshold] = label;
  });

  return labels;
};

const bmsmLabels = computed(() =>
  parseBmsmLabels(props?.data?.item?.tags ?? [])
);

const getPackSizeText = (threshold) => {
  const label = bmsmLabels.value?.[threshold];
  if (label) return label;
  return threshold > 1
    ? store?.$state?.terms?.bmsm?.pack_size_text + threshold
    : threshold;
};

const findTiers = () => {
  const tags = props?.data?.item?.tags;
  const bmsmTiers = [];
  if (tags?.find((tag) => tag.includes('Pack Size'))) {
    const bmsmTierData = tags
      .find((tag) => tag.includes('BMSM Tiers:'))
      ?.split(':')[1]
      .trim()
      .split('/');
    bmsmTierData?.forEach((tier) => {
      const tierData = tier.split('-');
      bmsmTiers.push({
        threshold: parseInt(tierData[0]),
        discount: parseInt(tierData[1]),
      });
    });
  }
  return bmsmTiers;
};

const bmsmTooltipText = computed(() => {
  const text = store?.$state?.terms?.tooltips?.bmsm;
  const tiers = findTiers();
  let newText = '';
  tiers.forEach((tier, i) => {
    const index = parseInt(i) + 1;
    if (tier.discount != 0) {
      const discountVariable = '{{discount}}';
      const thresholdVariable =
        tiers[index]?.threshold - tier.threshold > 1 || index == tiers.length
          ? '{{threshold}}'
          : '{{threshold}} or more';
      newText += text
        .replace(discountVariable, tier.discount)
        .replace(thresholdVariable, tier.threshold);
      if (index != tiers.length) newText += '<br/>';
    }
  });
  return newText;
});

const shipsBy = computed(() => {
  const date =
    store.isSteppedBundle && !isQV.value
      ? store?.getCurrentStep?.selected?.variant?.shipsBy?.value
      : props?.data?.selected?.variant?.shipsBy?.value;
  return date || false;
});

// Cart Functions live in src/modules/Cart.ts
const createEvent = (items) =>
  new CustomEvent('theme:add-to-cart', {
    detail: items,
  });

const findTprDiscount = (item, variant = false) => {
  const pogs = item?.promotedOptionGroups;
  if (!pogs) return 'null';
  const options = variant
    ? variant.selectedOptions
    : item?.variantBySelectedOptions?.selectedOptions;
  const values = options.map((option) => option.value);

  const index = pogs.findIndex((pog) =>
    pog.values.find((value) => values.includes(value))
  );

  if (index <= -1) return 'null';

  const tpr = pogs[index]?.tpr?.value ?? -1;
  const tprDiscount = parseInt(tpr);

  return tprDiscount > 0 ? tprDiscount : 'null';
};

const handleSubmit = async () => {
  let payload;
  const selections = isQV.value ? props.data.selected : store.$state.selected;
  const isBundle = (store.$state.isBundle && !isQV.value) ?? false;
  const hasComponents = isBundle && selections.components.length > 0;
  const item = isQV.value ? props.data.item : store.$state.item;

  handleSelectionMessaging(true);

  if (optionsToSelectError.value !== false) {
    return;
  }

  const promo = window.Brooklinen?.promo?.discounts ?? {};
  const categoryId = promo?.category?.promoName ?? 'null';

  if (hasComponents) {
    const title = store.$state.item.title;
    const bundleUrl = store.$state.item.onlineStoreUrl ?? '';
    const components = Array.from(selections.components);
    const timestamp = Date.now();

    //The checkout product handle is identical to the shell product's, with -handle appended to the end of it
    const checkoutProductHandle = `${store.$state.item.handle}-checkout`;
    const checkoutProductResponse = await fetchStorefront({
      variables: { handle: checkoutProductHandle },
      query: CHECKOUT_PRODUCT_QUERY,
    });
    const checkoutProductVariant =
      checkoutProductResponse?.data?.product?.variants?.edges?.[0]?.node?.id ??
      null;

    //pass a string null as the line item property if the checkout product variant does not exist
    const checkoutProductVariantId = checkoutProductVariant
      ? formatID(checkoutProductVariant)
      : 'null';

    payload = components
      .reduce((bundle, component, index) => {
        const variant =
          component.variantBySelectedOptions || component.variants[0];
        const variantId = variant.id
          .split('ProductVariant/')[1]
          .toString()
          .trim();
        const productId = variant.product.id
          .split('Product/')[1]
          .toString()
          .trim();
        const comparePrice = variant?.compareAtPrice?.amount;
        const price = variant.price.amount;
        const tags = component.tags;
        const shellTags = item.tags;

        // Bundle discount tag is only applied to the parent product so you need to grab parent's tags
        const bundleDiscount = shellTags
          .find((tag) => tag.includes('Discount Amount:'))
          ?.split(':')[1]
          .trim();

        let categoryDiscount = 'null';
        // Since some components of a bundle do not qualifiy for category sales you need to check for this tag at component level
        const isCategory = () =>
          tags.some((tag) => tag.includes('Category Promo:'));
        if (isCategory()) {
          categoryDiscount = tags
            .find((tag) => tag.includes('Category Promo:'))
            ?.split(':')[1]
            .trim();
        }

        // Temporary Price Reductions are component variants that get a reduction but are not part of any active sale ex: category sale
        const tprDiscount = findTprDiscount(component);

        const originalPrice = comparePrice > 1 ? comparePrice : price;
        const categoryPromoName = isCategory() ? categoryId : 'null';
        let savings = store.$state.bundleSavings[index];

        // For bundles tagged with Product Bundle No Discount
        if (isNaN(savings)) {
          savings = 0;
        }

        savings = savings.toString();

        const isStepped = store.isSteppedBundle;
        let shellVariant;

        if (isStepped) {
          const selectedShellVariant = store.$state.steps[0].selected.variant;
          shellVariant =
            typeof selectedShellVariant === 'number'
              ? selectedShellVariant
              : formatID(selectedShellVariant.id);
        } else {
          shellVariant = formatID(props.data.selected.variant.id);
        }

        const compItem = {
          id: parseInt(variantId),
          quantity: quantity.value,
          properties: {
            _component: (index + 1).toString(),
            _msrp: originalPrice,
            _product_bundle_title: title,
            _product_bundle_instance: `${timestamp}_${shellVariant}`,
            _product_bundle_items: components.length.toString(),
            _bundle_url: bundleUrl,
            _bundle_productId: productId,
            _bundle_variantId: variantId,
            _bundle_savings: savings,
            _category_discount: categoryDiscount,
            _category_id: categoryPromoName,
            _bundle_discount: bundleDiscount,
            _tpr_discount: String(tprDiscount),
            _checkout_product_variant_id: checkoutProductVariantId,
            Ships: variant?.shipsBy?.value,
          },
        };

        bundle.push(compItem);
        return bundle;
      }, [])
      .reverse();
    payload = { items: payload };
  } else {
    const variant = isQV.value
      ? props.data.selected.variant
      : store.$state.selected.variant;
    const id = variant.id.split('ProductVariant/')[1].toString().trim();
    const comparePrice = variant?.compareAtPrice?.amount;
    const price = variant.price.amount;
    const tags = props?.selectedProduct?.tags || item.tags;
    const originalPrice = comparePrice > 1 ? comparePrice : price;
    const tprReduction = comparePrice > 1;

    const isLastCall = () => tags.includes('Last Call Assortment');
    const isCategory = () => {
      const categoryTag = tags.some((tag) => tag.includes('Category Promo:'));
      // Adding the not last call check in the case that a category tag accidentally gets added to a last call prod. (Site ops accidentally does this sometimes, so this is a safeguard)
      return categoryTag && !isLastCall();
    };
    // Temporary Price Reductions are variants that get a reduction but can be part of an active sale

    let discount = 'null';
    let categoryDiscount = 'null';
    const tprDiscount = findTprDiscount(item, variant);
    const categoryPromoName = isCategory() ? categoryId : 'null';
    const bmsmGroup = parseTag(tags, 'BMSM Group: ') ?? 'null';
    const bmsmTiers = parseTag(tags, 'BMSM Tiers: ') ?? 'null';

    if (isCategory()) {
      categoryDiscount = tags
        .find((tag) => tag.includes('Category Promo:'))
        ?.split(':')[1]
        .trim();
    }
    if (isLastCall()) {
      discount = tags
        .find((tag) => tag.includes('Discount Amount:'))
        ?.split(':')[1]
        .trim();
    }

    payload = {
      id: parseInt(id),
      quantity: quantity.value,
      properties: {
        _msrp: originalPrice,
        _category_id: categoryPromoName,
        _category_discount: categoryDiscount,
        _isGiftCard: props.isGiftCard.toString(),
        Ships: variant?.shipsBy?.value,
        _lc_discount: discount,
        _tpr_discount: String(tprDiscount),
        _bmsm_group: bmsmGroup,
        _bmsm_tiers: bmsmTiers,
      },
    };
  }

  const event = createEvent(payload);
  document.dispatchEvent(event);

  if (isQV.value) {
    store.$state.isQV = false;
    return emits('close');
  }
};

const handleEdit = async () => {
  const handle = props?.data?.selected.handle;
  // If an autobundle contains two of the same component, use stored index to target the correct component instead of product handle
  const storedIndex = props?.data?.selected?.editIndex;
  const index =
    storedIndex !== undefined
      ? storedIndex
      : store.$state.components.findIndex((component) =>
          component.includes(handle)
        );

  const options = props.data.selected.variant.selectedOptions;
  const variables = { handle, options };

  const request = { variables, query: COMPONENT_FROM_SELECTIONS_QUERY };

  const response = await fetchStorefront(request).then((res) => {
    const item = clean(res?.data?.product) ?? false;
    if (item?.promotedOptionGroups) {
      const groups = store.formatPOGs(item);
      item.promotedOptionGroups = groups;
    }
    return item;
  });
  store.$state.selected.components[index] = response;
  store.handleComponentAvailability();
  return emits('close');
};

const available = computed(() => {
  const isEditing = props?.edit ?? false;

  if (props.isGiftCard) return true;
  if (isEditing) {
    const item = props?.data ?? false;
    if (!item) return false;

    const variant = item?.selected?.variant;
    const isAvailable = variant.quantityAvailable > 0;
    return isAvailable;
  }

  const isStepped = store.isSteppedBundle;
  if (isStepped) {
    const current = store.getCurrentStep;
    const atReview = store.isReviewStep;
    if (atReview) return true;
    return current.selected.available;
  } else {
    return store.$state.selected.available;
  }
});

const maxAllowedQuantity = computed(() => {
  const isSteppedBundle = store.isSteppedBundle && !isQV.value;
  const themeSetting = parseInt(store.$state.settings.max_inventory);
  if (isSteppedBundle) return store.createRange(themeSetting);

  const data = props?.data ? props.data : store.$state;
  const variant = data.selected.variant ?? false;
  if (!variant) return store.createRange(themeSetting);

  const variantInventory = variant?.quantityAvailable ?? false;
  const hasLessInventory = variantInventory < themeSetting;

  if (hasLessInventory && variantInventory > 0) {
    return store.createRange(variantInventory);
  } else {
    return store.createRange(themeSetting);
  }
});

const handleCtaMessaging = () => {
  const isBuildStep = store.isBuildStep;
  const isEditing = props?.edit ?? false;
  switch (true) {
    case isEditing:
      const variant = props?.data?.selected?.variant ?? false;
      const hasInventory = variant.quantityAvailable > 0;
      cta.value = store.$state.terms.cta.edit;
      if (hasInventory) {
        if (isQV.value) cta.value = store.$state.terms.cta.default;
        else cta.value = store.$state.terms.cta.edit;
      } else {
        cta.value = store.$state.terms.cta.unavailable;
      }
      break;
    case isBuildStep:
      cta.value = store.$state.terms.cta.build;
      break;
    default:
      if (!available.value) {
        cta.value = store.$state.terms.cta.unavailable;
      } else {
        cta.value = store.$state.terms.cta.default;
      }
      break;
  }
};

const handleSelectionMessaging = (overwrite = false) => {
  const selections = isQV.value ? props.data.selected : store.$state.selected;
  const item = isQV.value ? props.data.item : store.$state.item;

  const unselectedOptions = selections.options.filter(
    (option) => option === ''
  );
  if (unselectedOptions.length > 0 && overwrite) {
    const unselectedIndex = selections.options.findIndex(
      (option) => option === ''
    );
    const unselectedOption = item.options[unselectedIndex].name;
    optionsToSelectError.value = store.$state.terms.cta.error.replace(
      '{{option}}',
      unselectedOption
    );
  } else {
    optionsToSelectError.value = false;
  }
};

const readyToProceed = computed(() => store.stepStatus);
const nextCta = computed(() => {
  const current = store.getCurrentStep;
  const { totalOptions } = current;
  const { options, available } = current.selected;
  const selections = options.filter((option) => option !== '');
  const enoughSelectionsMade = selections.length >= totalOptions;
  if (enoughSelectionsMade && !available) {
    return store.$state.terms.cta.unavailable;
  } else {
    return store.$state.terms.cta.next;
  }
});

watchEffect(() => handleSelectionMessaging());
watchEffect(() => handleCtaMessaging());
watchEffect(() => {
  if (ctaEl.value) {
    const event = new CustomEvent('cta:updated');
    document.dispatchEvent(event);
  }
});
</script>
